/**
 * Parses the trace JSON the Observability API passes through from Tempo
 * (`/api/traces/{id}`): OTLP-JSON with `batches` (Tempo v1 API) or `resourceSpans`
 * (OTLP / Tempo v2, optionally under `trace`), spans under `scopeSpans` or the
 * older `instrumentationLibrarySpans`. Span IDs arrive base64-encoded in Tempo's
 * JSON and hex-encoded in plain OTLP; both are normalised to hex.
 */

export interface TraceSpan {
  id: string;
  parentId: string | null;
  name: string;
  service: string;
  kind: string | null;
  startMs: number;
  endMs: number;
  durationMs: number;
  attributes: Record<string, string>;
  error: string | null;
  depth: number;
}

export interface ParsedTrace {
  spans: TraceSpan[];
  startMs: number;
  endMs: number;
  services: string[];
  errorCount: number;
}

type Json = Record<string, unknown>;

const isObj = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

function toHexId(raw: unknown): string {
  if (typeof raw !== 'string' || raw === '') return '';
  if (/^[0-9a-f]+$/i.test(raw) && (raw.length === 16 || raw.length === 32)) return raw.toLowerCase();
  try {
    return Array.from(atob(raw), (c) => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
  } catch {
    return raw;
  }
}

function nanosToMs(raw: unknown): number {
  if (typeof raw === 'number') return raw / 1e6;
  if (typeof raw === 'string' && /^\d+$/.test(raw)) {
    const n = BigInt(raw);
    return Number(n / 1000n) / 1000;
  }
  return 0;
}

function attrValue(v: unknown): string {
  if (!isObj(v)) return String(v ?? '');
  for (const k of ['stringValue', 'intValue', 'doubleValue', 'boolValue']) if (k in v) return String(v[k]);
  if ('arrayValue' in v && isObj(v.arrayValue)) return arr(v.arrayValue.values).map(attrValue).join(', ');
  return JSON.stringify(v);
}

function attrs(list: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const a of arr(list)) if (isObj(a) && typeof a.key === 'string') out[a.key] = attrValue(a.value);
  return out;
}

function kindName(kind: unknown): string | null {
  if (typeof kind === 'string') return kind.replace(/^SPAN_KIND_/, '').toLowerCase();
  const names = ['unspecified', 'internal', 'server', 'client', 'producer', 'consumer'];
  return typeof kind === 'number' ? (names[kind] ?? null) : null;
}

function statusError(status: unknown): string | null {
  if (!isObj(status)) return null;
  const code = status.code;
  if (code === 'STATUS_CODE_ERROR' || code === 2) return typeof status.message === 'string' && status.message ? status.message : 'error';
  return null;
}

/** Returns null when the payload has no recognisable spans. */
export function parseTempoTrace(payload: unknown): ParsedTrace | null {
  const root = isObj(payload) && isObj(payload.trace) ? payload.trace : payload;
  if (!isObj(root)) return null;
  const batches = arr(root.batches).length ? arr(root.batches) : arr(root.resourceSpans);
  const raw: Omit<TraceSpan, 'depth'>[] = [];
  for (const b of batches) {
    if (!isObj(b)) continue;
    const resource = isObj(b.resource) ? attrs(b.resource.attributes) : {};
    const service = resource['service.name'] ?? 'unknown';
    const scopes = arr(b.scopeSpans).length ? arr(b.scopeSpans) : arr(b.instrumentationLibrarySpans);
    for (const sc of scopes) {
      if (!isObj(sc)) continue;
      for (const s of arr(sc.spans)) {
        if (!isObj(s)) continue;
        const startMs = nanosToMs(s.startTimeUnixNano);
        const endMs = nanosToMs(s.endTimeUnixNano);
        raw.push({
          id: toHexId(s.spanId),
          parentId: toHexId(s.parentSpanId) || null,
          name: typeof s.name === 'string' ? s.name : '(unnamed)',
          service,
          kind: kindName(s.kind),
          startMs,
          endMs,
          durationMs: Math.max(0, endMs - startMs),
          attributes: attrs(s.attributes),
          error: statusError(s.status),
        });
      }
    }
  }
  if (raw.length === 0) return null;

  // Depth-first order by start time, so the waterfall reads top-down.
  const ids = new Set(raw.map((s) => s.id));
  const children = new Map<string | null, Omit<TraceSpan, 'depth'>[]>();
  for (const s of raw) {
    const parent = s.parentId && ids.has(s.parentId) ? s.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), s]);
  }
  const spans: TraceSpan[] = [];
  const visit = (parent: string | null, depth: number) => {
    for (const s of [...(children.get(parent) ?? [])].sort((a, b) => a.startMs - b.startMs)) {
      spans.push({ ...s, depth });
      if (depth < 64) visit(s.id, depth + 1);
    }
  };
  visit(null, 0);

  const startMs = Math.min(...spans.map((s) => s.startMs));
  const endMs = Math.max(...spans.map((s) => s.endMs));
  return {
    spans,
    startMs,
    endMs,
    services: [...new Set(spans.map((s) => s.service))],
    errorCount: spans.filter((s) => s.error).length,
  };
}

export function formatMs(ms: number): string {
  if (ms >= 60_000) return `${(ms / 60_000).toFixed(1)} min`;
  if (ms >= 1000) return `${(ms / 1000).toFixed(2)} s`;
  if (ms >= 1) return `${ms.toFixed(1)} ms`;
  return `${(ms * 1000).toFixed(0)} µs`;
}
